/**
 * Auth session storage that degrades instead of crashing.
 *
 * Reading `window.localStorage` throws (SecurityError) when storage is blocked
 * or partitioned — common in the embedded webviews desktop mail clients use to
 * open confirmation links, and whenever a user blocks all cookies. Referencing
 * it at module scope therefore took down the whole bundle before React mounted,
 * which surfaced as a blank page.
 *
 * Falls back to an in-memory store: auth still works for the current page, it
 * just doesn't survive a reload.
 */

type MinimalStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const createMemoryStorage = (): MinimalStorage => {
  const store = new Map<string, string>();

  return {
    getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
    setItem: (key, value) => {
      store.set(key, value);
    },
    removeItem: (key) => {
      store.delete(key);
    },
  };
};

const memoryStorage = createMemoryStorage();

const resolveLocalStorage = (): MinimalStorage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }

    // Probe writability too — some contexts expose the object but reject writes.
    const probeKey = "__nfp_storage_probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);

    return window.localStorage;
  } catch {
    return null;
  }
};

export const resilientAuthStorage: MinimalStorage = {
  getItem: (key) => {
    try {
      return (resolveLocalStorage() ?? memoryStorage).getItem(key);
    } catch {
      return memoryStorage.getItem(key);
    }
  },
  setItem: (key, value) => {
    try {
      (resolveLocalStorage() ?? memoryStorage).setItem(key, value);
    } catch {
      memoryStorage.setItem(key, value);
    }
  },
  removeItem: (key) => {
    try {
      (resolveLocalStorage() ?? memoryStorage).removeItem(key);
    } catch {
      memoryStorage.removeItem(key);
    }
  },
};
